/**
 * The subtitle editor, as a component any page can mount on a document the worker holds.
 *
 * /subtitle-editor mounts it on the file the visitor opened; the auto subtitle generator will mount
 * it on the document its transcription produced (SEO_BRIEF_04 item 14). Either way the caller hands
 * over a `DocumentId` and gets the SAME id back: every edit and every undo replaces the document
 * under its own id in the worker (src/editor/session.ts), so the id the caller already holds is the
 * edited document, ready for `write`, `apply` or a burn-in. `flush()` waits for the edits in flight.
 *
 *   const editor = mountCueEditor(root, { client, docId, cueCount });
 *   …
 *   const id = await editor.flush();          // the edited document, same id
 *   const file = await editor.write('srt', options);
 *   editor.destroy();
 *
 * WHAT IS ON THIS SIDE. A window of rows, never a document (contracts/adapter.md §1): the table is a
 * fixed pool of row elements recycled over the scroll position (lib/virtual.ts does the arithmetic),
 * so a file of 40,000 subtitles costs the same DOM as one of forty. Every change names a cue by id,
 * so a click on a row that was a stale copy is refused by the worker, never misapplied.
 *
 * WORDS. Every sentence comes from the markup (`[data-editor-label]` and the row `<template>`); this
 * file holds codes only.
 */

import type { DocumentId, WriteFormat, WriteOptions } from '../../../../../contracts/cue-model.ts';
import type { WritePayload } from '../../../../../src/core/protocol.ts';
import { TICKS_PER_SECOND } from '../../../../../src/core/time/ticks.ts';
import type { EditorAction, EditorPayload } from '../../../../../src/editor/session.ts';
import type { SubtitleWorkerClient } from '../../../../../src/worker/client.ts';
import {
  commitTime,
  createEditorWindow,
  type Draft,
  type DraftError,
  type EditorRow,
  editorKey,
  rowView,
  secondsToTicks,
  splitAt,
  type TimeField,
  ticksToSeconds,
} from './cue-editor-model.ts';
import { must, on, qs, setData } from './dom.ts';
import { poolSize, type Range, sameRange, sizerHeight, slotFor, windowRange } from './virtual.ts';

/** The one method of the worker client the editor uses. A test passes a stand-in. */
export type EditorClient = Pick<SubtitleWorkerClient, 'request'>;

export interface EditorState {
  /** The same id the editor was opened with, holding the document as edited. */
  readonly docId: DocumentId;
  readonly cueCount: number;
  readonly undoDepth: number;
  readonly redoDepth: number;
  /** Moves on every change, undo and redo: a saved copy older than this is stale. */
  readonly version: number;
}

export interface CueEditorOptions {
  readonly client: EditorClient;
  readonly docId: DocumentId;
  readonly cueCount: number;
  /** After every change, undo and redo. */
  readonly onChange?: (state: EditorState) => void;
  /** A worker failure the editor cannot explain itself (a crash, a lost document). */
  readonly onError?: (err: unknown) => void;
}

export interface CueEditorHandle {
  readonly docId: DocumentId;
  state(): EditorState;
  undo(): Promise<void>;
  redo(): Promise<void>;
  /** Resolves once every edit sent so far has landed, with the id that holds the result. */
  flush(): Promise<DocumentId>;
  /** The edited document, written. Waits for edits in flight first. */
  write(format: WriteFormat, options: WriteOptions): Promise<WritePayload>;
  /** Play a local file beside the list (an object URL; never read or sent), or remove it. */
  setVideo(file: File | null): void;
  destroy(): void;
}

const OVERSCAN = 4;
const FALLBACK_ROW_HEIGHT = 120;
/** How often a playing video asks which cue is on screen. */
const LOCATE_EVERY_MS = 200;
/** How long after the last change the video's subtitle track is rebuilt. */
const TRACK_DEBOUNCE_MS = 400;

/** The VTT the preview's <track> is built from: the same write any download uses, in LF. */
function trackOptions(): WriteOptions {
  return {
    format: 'vtt',
    framerate: null,
    trackId: null,
    wordTiming: 'where-available',
    karaoke: null,
    maxLines: null,
    anchorAsSrtTag: false,
    bom: false,
    encoding: 'utf-8',
    eol: '\n',
    useRawPassthrough: false,
    formatOptions: null,
  };
}

interface RowNodes {
  readonly root: HTMLElement;
  readonly n: HTMLElement;
  readonly start: HTMLInputElement;
  readonly end: HTMLInputElement;
  readonly text: HTMLTextAreaElement;
  readonly error: HTMLElement;
  /** The row index this element renders, or -1 when parked. */
  index: number;
  row: EditorRow | null;
}

type FieldEl = HTMLInputElement | HTMLTextAreaElement;

/** Value without clobbering typing: a focused box with uncommitted text keeps what is in it. */
function setField(el: FieldEl, value: string, force: boolean): void {
  const typing = document.activeElement === el && el.value !== el.defaultValue;
  if (typing && !force) return;
  if (el.defaultValue !== value) el.defaultValue = value;
  if (el.value !== value) el.value = value;
}

export function mountCueEditor(root: HTMLElement, options: CueEditorOptions): CueEditorHandle {
  const { client, docId } = options;
  const scroller = must<HTMLElement>('[data-editor-scroller]', root);
  const sizer = must<HTMLElement>('[data-editor-sizer]', root);
  const windowEl = must<HTMLElement>('[data-editor-window]', root);
  const table = must<HTMLElement>('[data-editor-table]', root);
  const rowTemplate = must<HTMLTemplateElement>('template[data-editor-row]', root);
  const empty = qs<HTMLElement>('[data-editor-empty]', root);
  const status = qs<HTMLElement>('[data-editor-status]', root);
  const undoButton = qs<HTMLButtonElement>('[data-editor-undo]', root);
  const redoButton = qs<HTMLButtonElement>('[data-editor-redo]', root);
  const insertFirst = qs<HTMLButtonElement>('[data-editor-insert-first]', root);
  const videoPane = qs<HTMLElement>('[data-editor-video]', root);
  const player = qs<HTMLVideoElement>('[data-editor-player]', root);
  const videoName = qs<HTMLElement>('[data-editor-video-name]', root);
  const videoInput = qs<HTMLInputElement>('[data-editor-video-input]', root);
  const videoRemove = qs<HTMLButtonElement>('[data-editor-video-remove]', root);

  const label = (key: string): string =>
    qs<HTMLElement>(`[data-editor-label="${key}"]`, root)?.textContent?.trim() ?? key;

  let cueCount = options.cueCount;
  let undoDepth = 0;
  let redoDepth = 0;
  let version = 0;
  let selectedId: string | null = null;
  let currentId: string | null = null;
  /** Typed and not accepted, by cue id. */
  const drafts = new Map<string, Draft>();
  /** Where the caret was in a cue's words when focus last left them, for "split at the cursor". */
  let caret: { readonly id: string; readonly at: number } | null = null;
  /** Insert asks for the new row's words box to take focus once it is painted. */
  let focusText: string | null = null;
  let queue: Promise<void> = Promise.resolve();
  let destroyed = false;

  const rows = createEditorWindow({
    fetchPage: (from, count) => client.request('preview', { docId, from, count }),
    onPage: () => paint(),
  });
  rows.reset(docId, cueCount);

  /* ── the table ─────────────────────────────────────────────────────────── */

  const pool: RowNodes[] = [];
  let rowHeight = FALLBACK_ROW_HEIGHT;
  let rendered: Range = { start: -1, end: -1, offset: 0 };
  let frame = 0;
  /**
   * Whether a real row has been measured. The first rows arrive a round trip after mount, when
   * nothing resizes, so the first paint that shows one measures it.
   */
  let measured = false;

  function buildRow(): RowNodes {
    const fragment = rowTemplate.content.cloneNode(true) as DocumentFragment;
    const rowRoot = fragment.firstElementChild as HTMLElement;
    windowEl.append(rowRoot);
    return {
      root: rowRoot,
      n: must<HTMLElement>('[data-slot="n"]', rowRoot),
      start: must<HTMLInputElement>('[data-field="start"]', rowRoot),
      end: must<HTMLInputElement>('[data-field="end"]', rowRoot),
      text: must<HTMLTextAreaElement>('[data-field="text"]', rowRoot),
      error: must<HTMLElement>('[data-slot="error"]', rowRoot),
      index: -1,
      row: null,
    };
  }

  function measure(): void {
    const probe = pool.find((node) => !node.root.hidden);
    if (!probe) return;
    if (scroller.clientWidth === 0 || scroller.clientHeight === 0) return;
    const height = probe.root.getBoundingClientRect().height;
    if (height > 0) measured = true;
    if (height > 0 && Math.abs(height - rowHeight) > 0.5) {
      rowHeight = height;
      sizer.style.height = `${sizerHeight(rows.count(), rowHeight)}px`;
      for (const node of pool) node.index = -1;
    }
  }

  function park(node: RowNodes): void {
    if (node.root.contains(document.activeElement)) (document.activeElement as HTMLElement).blur();
    node.root.hidden = true;
    node.index = -1;
    node.row = null;
  }

  function write(node: RowNodes, index: number, row: EditorRow): void {
    // A box being typed in is about to show another cue: commit it first, by blurring it, which
    // is exactly what moving away from it by hand would do.
    const sameCue = node.row?.id === row.id;
    if (!sameCue && node.root.contains(document.activeElement)) {
      (document.activeElement as HTMLElement).blur();
    }
    const view = rowView(row, drafts.get(row.id), selectedId, currentId);
    node.root.hidden = false;
    node.root.style.transform = `translate3d(0, ${index * rowHeight}px, 0)`;
    node.root.setAttribute('aria-rowindex', String(index + 1));
    setData(node.root, 'cue-id', row.id);
    node.n.textContent = view.n;
    setField(node.start, view.start, !sameCue);
    setField(node.end, view.end, !sameCue);
    setField(node.text, view.text, !sameCue);
    node.start.setAttribute('aria-invalid', String(view.startInvalid));
    node.end.setAttribute('aria-invalid', String(view.endInvalid));
    node.error.textContent = view.error === null ? '' : label(view.error);
    setData(node.root, 'selected', view.selected ? 'true' : null);
    setData(node.root, 'current', view.current ? 'true' : null);
    node.index = index;
    node.row = row;
    if (focusText === row.id) {
      focusText = null;
      node.text.focus();
    }
  }

  function paint(): void {
    if (destroyed) return;
    const count = rows.count();
    const viewport = scroller.clientHeight || 1;
    const range = windowRange(scroller.scrollTop, viewport, rowHeight, count, OVERSCAN);
    const wanted = Math.min(poolSize(viewport, rowHeight, OVERSCAN), Math.max(count, 1));
    if (wanted > pool.length) {
      while (pool.length < wanted) pool.push(buildRow());
      for (const node of pool) node.index = -1;
    }
    for (let index = range.start; index < range.end; index += 1) {
      const node = pool[slotFor(index, pool.length)];
      if (!node) continue;
      const row = rows.row(index);
      if (!row) {
        // Not loaded yet. Keep what the element shows until its page lands, unless it shows a
        // different row entirely.
        if (node.index !== index) park(node);
        continue;
      }
      if (node.index === index && node.row === row) continue;
      write(node, index, row);
    }
    for (const node of pool) {
      if (node.index >= range.start && node.index < range.end) continue;
      if (!node.root.hidden) park(node);
    }
    rendered = range;
    if (!measured) {
      const before = rowHeight;
      measure();
      if (rowHeight !== before) paint();
    }
  }

  function refresh(): void {
    const count = rows.count();
    table.setAttribute('aria-rowcount', String(count));
    if (empty) empty.hidden = count > 0;
    scroller.hidden = count === 0;
    sizer.style.height = `${sizerHeight(count, rowHeight)}px`;
    // No reset of what each element shows: after an edit the window keeps answering with the
    // rows it held until the fresh ones land, and an element already showing that very row
    // object is left alone, so an edit repaints in place rather than flickering back first.
    rendered = { start: -1, end: -1, offset: 0 };
    paint();
    measure();
    paint();
  }

  /** Repaint only the selection and the playing marker, without touching any box. */
  function paintMarks(): void {
    for (const node of pool) {
      if (node.row === null || node.root.hidden) continue;
      setData(node.root, 'selected', node.row.id === selectedId ? 'true' : null);
      setData(node.root, 'current', node.row.id === currentId ? 'true' : null);
    }
  }

  function ensureVisible(index: number): void {
    const top = index * rowHeight;
    const viewport = scroller.clientHeight || rowHeight;
    if (top >= scroller.scrollTop && top + rowHeight <= scroller.scrollTop + viewport) return;
    scroller.scrollTop = Math.max(0, top - Math.max(0, (viewport - rowHeight) / 3));
    paint();
  }

  function onScroll(): void {
    if (frame) return;
    frame = requestAnimationFrame(() => {
      frame = 0;
      const next = windowRange(
        scroller.scrollTop,
        scroller.clientHeight || 1,
        rowHeight,
        rows.count(),
        OVERSCAN,
      );
      if (sameRange(next, rendered)) return;
      paint();
    });
  }

  /* ── talking to the worker ─────────────────────────────────────────────── */

  function say(text: string): void {
    if (status) status.textContent = text;
  }

  function buttons(): void {
    if (undoButton) undoButton.disabled = undoDepth === 0;
    if (redoButton) redoButton.disabled = redoDepth === 0;
  }

  function state(): EditorState {
    return { docId, cueCount, undoDepth, redoDepth, version };
  }

  /** The cue a times edit was for, so a refusal lands on its boxes. */
  function refusedTimes(action: EditorAction, code: DraftError): boolean {
    if (action.kind !== 'edit' || action.edit.kind !== 'times') return false;
    const { cueId } = action.edit;
    const field: TimeField = action.edit.start === undefined ? 'end' : 'start';
    const node = pool.find((n) => n.row?.id === cueId);
    drafts.set(cueId, {
      ...(node ? { start: node.start.value, end: node.end.value } : {}),
      error: code,
      errorField: field,
    });
    return true;
  }

  function land(payload: EditorPayload, action: EditorAction): void {
    undoDepth = payload.undoDepth;
    redoDepth = payload.redoDepth;
    if (payload.refusal !== null) {
      if (!refusedTimes(action, payload.refusal)) say(label(payload.refusal));
      for (const node of pool) node.index = -1;
      paint();
    }
    if (payload.changed) {
      version += 1;
      cueCount = payload.cueCount;
      rows.invalidate(cueCount);
      if (payload.focus) selectedId = payload.focus.id;
      refresh();
      if (payload.focus) ensureVisible(payload.focus.index);
      scheduleTrack();
      options.onChange?.(state());
    }
    buttons();
  }

  function send(action: EditorAction): Promise<void> {
    const next = queue.then(async () => {
      if (destroyed) return;
      const payload = await client.request('editor', { docId, action });
      if (destroyed) return;
      land(payload, action);
    });
    // The queue survives a failure: the next edit still goes, after this one.
    queue = next.catch((err: unknown) => {
      options.onError?.(err);
    });
    return queue;
  }

  function edit(e: Extract<EditorAction, { kind: 'edit' }>['edit']): Promise<void> {
    say('');
    return send({ kind: 'edit', edit: e, from: selectedId });
  }

  async function undo(): Promise<void> {
    const had = undoDepth;
    drafts.clear();
    await send({ kind: 'undo' });
    say(label(had === 0 ? 'nothing-to-undo' : 'undone'));
  }

  async function redo(): Promise<void> {
    if (redoDepth === 0) return;
    drafts.clear();
    await send({ kind: 'redo' });
    say(label('redone'));
  }

  /* ── what a row's boxes do ─────────────────────────────────────────────── */

  function nodeOf(target: EventTarget | null): RowNodes | null {
    const hit = target instanceof Element ? target.closest('.cue-editor__row') : null;
    return pool.find((node) => node.root === hit && node.row !== null) ?? null;
  }

  function fieldOf(target: EventTarget | null): 'start' | 'end' | 'text' | null {
    if (!(target instanceof HTMLElement)) return null;
    const field = target.getAttribute('data-field');
    return field === 'start' || field === 'end' || field === 'text' ? field : null;
  }

  function commitText(node: RowNodes): void {
    const row = node.row;
    if (row === null) return;
    const typed = node.text.value;
    if (typed === node.text.defaultValue) return;
    node.text.defaultValue = typed;
    void edit({ kind: 'text', cueId: row.id, text: typed });
  }

  function commitTimeField(node: RowNodes, field: TimeField): void {
    const row = node.row;
    if (row === null) return;
    const box = field === 'start' ? node.start : node.end;
    const draft = drafts.get(row.id);
    if (box.value === box.defaultValue && draft?.[field] === undefined) return;
    const otherField: TimeField = field === 'start' ? 'end' : 'start';
    const result = commitTime(row, field, box.value, draft?.[otherField]);
    if (result.kind === 'same') {
      drafts.delete(row.id);
    } else if (result.kind === 'invalid') {
      drafts.set(row.id, {
        ...draft,
        [field]: box.value,
        error: result.code,
        errorField: result.field,
      });
    } else {
      drafts.delete(row.id);
      // Accepted as typed until the worker's answer repaints the row in the table's own form.
      box.defaultValue = box.value;
      void edit({
        kind: 'times',
        cueId: row.id,
        ...(result.start === undefined ? {} : { start: result.start }),
        ...(result.end === undefined ? {} : { end: result.end }),
      });
      return;
    }
    write(node, node.index, row);
  }

  function revert(node: RowNodes, field: 'start' | 'end' | 'text'): void {
    const row = node.row;
    if (row === null) return;
    if (field !== 'text') drafts.delete(row.id);
    const box = field === 'start' ? node.start : field === 'end' ? node.end : node.text;
    box.value = box.defaultValue;
    write(node, node.index, row);
  }

  function select(node: RowNodes): void {
    const row = node.row;
    if (row === null || row.id === selectedId) return;
    selectedId = row.id;
    if (caret !== null && caret.id !== row.id) caret = null;
    paintMarks();
    // A paused video shows the frame the line starts on.
    if (player && hasVideo && player.paused) {
      player.currentTime = ticksToSeconds(row.start, TICKS_PER_SECOND);
    }
  }

  const offs: (() => void)[] = [];

  offs.push(
    on(windowEl, 'focusin', (ev) => {
      const node = nodeOf(ev.target);
      if (node) select(node);
    }),
    on(windowEl, 'focusout', (ev) => {
      const node = nodeOf(ev.target);
      const field = fieldOf(ev.target);
      if (!node || !field || !node.row) return;
      if (field === 'text') {
        caret = { id: node.row.id, at: node.text.selectionStart };
        commitText(node);
      } else {
        commitTimeField(node, field);
      }
    }),
    on(windowEl, 'input', (ev) => {
      // Live: a box that cannot be read is marked as you type, and unmarked once it can.
      const node = nodeOf(ev.target);
      const field = fieldOf(ev.target);
      if (!node?.row || (field !== 'start' && field !== 'end')) return;
      const box = field === 'start' ? node.start : node.end;
      const check = commitTime(node.row, field, box.value, undefined);
      const unreadable = check.kind === 'invalid' && check.code !== 'end-not-after-start';
      box.setAttribute('aria-invalid', String(unreadable));
      node.error.textContent = unreadable && check.kind === 'invalid' ? label(check.code) : '';
    }),
    on(windowEl, 'keydown', (ev) => {
      const key = ev as KeyboardEvent;
      const node = nodeOf(key.target);
      const field = fieldOf(key.target);
      if (!node || !field) return;
      if (key.key === 'Escape') {
        key.preventDefault();
        revert(node, field);
        return;
      }
      if (key.key !== 'Enter') return;
      if (field === 'text') {
        // Enter is a line break in the words; Ctrl/Cmd+Enter keeps the change.
        if (!(key.ctrlKey || key.metaKey)) return;
        key.preventDefault();
        commitText(node);
        return;
      }
      key.preventDefault();
      commitTimeField(node, field);
    }),
    on(windowEl, 'click', (ev) => {
      const target = ev.target instanceof Element ? ev.target.closest('[data-act]') : null;
      if (!target) return;
      const node = nodeOf(target);
      const row = node?.row;
      if (!node || !row) return;
      select(node);
      switch (target.getAttribute('data-act')) {
        case 'seek':
          if (player && hasVideo) {
            player.currentTime = ticksToSeconds(row.start, TICKS_PER_SECOND);
            void player.play().catch(() => undefined);
          }
          return;
        case 'split': {
          const at = caret !== null && caret.id === row.id ? caret.at : null;
          void edit({ kind: 'split', cueId: row.id, at: splitAt(node.text.value, at) });
          caret = null;
          return;
        }
        case 'merge':
          void edit({ kind: 'merge', cueId: row.id });
          return;
        case 'insert':
          void edit({ kind: 'insert', after: row.id }).then(() => {
            focusText = selectedId;
            paint();
          });
          return;
        case 'delete':
          void edit({ kind: 'delete', cueId: row.id });
          return;
        default:
          return;
      }
    }),
    on(root, 'keydown', (ev) => {
      const key = ev as KeyboardEvent;
      const target = key.target;
      const dirty =
        (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) &&
        target.value !== target.defaultValue;
      const action = editorKey(key, dirty);
      if (action === null) return;
      key.preventDefault();
      if (target instanceof HTMLElement && target.closest('.cue-editor__row')) target.blur();
      void (action === 'undo' ? undo() : redo());
    }),
    on(scroller, 'scroll', onScroll, { passive: true }),
  );
  if (undoButton) offs.push(on(undoButton, 'click', () => void undo()));
  if (redoButton) offs.push(on(redoButton, 'click', () => void redo()));
  if (insertFirst) {
    offs.push(
      on(insertFirst, 'click', () => {
        void edit({ kind: 'insert', after: null }).then(() => {
          focusText = selectedId;
          paint();
        });
      }),
    );
  }

  const resizeObserver =
    typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(() => {
          measure();
          paint();
        })
      : null;
  resizeObserver?.observe(scroller);

  /* ── the video ─────────────────────────────────────────────────────────── */

  let hasVideo = false;
  let videoUrl: string | null = null;
  let trackUrl: string | null = null;
  let trackTimer = 0;
  let trackVersion = -1;
  let locating = false;
  let lastLocate = 0;

  async function buildTrack(): Promise<void> {
    if (!player || !hasVideo || trackVersion === version) return;
    const building = version;
    await queue;
    const written = await client.request('write', {
      docId,
      format: 'vtt',
      options: trackOptions(),
    });
    if (destroyed || !hasVideo || building !== version) return;
    trackVersion = building;
    const text =
      typeof written.output === 'string'
        ? written.output
        : new TextDecoder().decode(written.output);
    const url = URL.createObjectURL(new Blob([text], { type: 'text/vtt' }));
    for (const old of Array.from(player.querySelectorAll('track'))) old.remove();
    if (trackUrl) URL.revokeObjectURL(trackUrl);
    trackUrl = url;
    const track = document.createElement('track');
    track.kind = 'subtitles';
    track.label = table.getAttribute('aria-label') ?? '';
    track.srclang = 'und';
    track.default = true;
    track.src = url;
    player.append(track);
    track.track.mode = 'showing';
  }

  function scheduleTrack(): void {
    if (!hasVideo) return;
    clearTimeout(trackTimer);
    trackTimer = window.setTimeout(() => {
      void buildTrack().catch((err: unknown) => options.onError?.(err));
    }, TRACK_DEBOUNCE_MS);
  }

  function editingInTable(): boolean {
    return windowEl.contains(document.activeElement);
  }

  async function locate(): Promise<void> {
    if (!player || !hasVideo || locating) return;
    const now = Date.now();
    if (now - lastLocate < LOCATE_EVERY_MS) return;
    lastLocate = now;
    locating = true;
    try {
      const at = secondsToTicks(player.currentTime, TICKS_PER_SECOND);
      const payload = await client.request('editor', {
        docId,
        action: { kind: 'locate', at },
      });
      if (destroyed) return;
      const next = payload.focus?.id ?? null;
      if (next !== currentId) {
        currentId = next;
        paintMarks();
      }
      if (payload.focus && !player.paused && !editingInTable()) ensureVisible(payload.focus.index);
    } catch {
      // A missed tick is not worth a banner; the next one asks again.
    } finally {
      locating = false;
    }
  }

  function setVideo(file: File | null): void {
    if (!player || !videoPane) return;
    if (videoUrl) URL.revokeObjectURL(videoUrl);
    videoUrl = null;
    for (const old of Array.from(player.querySelectorAll('track'))) old.remove();
    if (trackUrl) URL.revokeObjectURL(trackUrl);
    trackUrl = null;
    trackVersion = -1;
    currentId = null;
    if (file === null) {
      hasVideo = false;
      player.removeAttribute('src');
      player.load();
      videoPane.hidden = true;
      if (videoName) videoName.textContent = '';
      paintMarks();
      return;
    }
    hasVideo = true;
    // An object URL: the browser plays the file from the visitor's own disk. Nothing reads it.
    videoUrl = URL.createObjectURL(file);
    player.src = videoUrl;
    videoPane.hidden = false;
    if (videoName) videoName.textContent = file.name;
    say('');
    void buildTrack().catch((err: unknown) => options.onError?.(err));
  }

  if (player) {
    offs.push(
      on(player, 'timeupdate', () => void locate()),
      on(player, 'seeked', () => {
        lastLocate = 0;
        void locate();
      }),
      on(player, 'error', () => {
        if (hasVideo) say(label('video-failed'));
      }),
    );
  }
  if (videoInput) {
    offs.push(
      on(videoInput, 'change', () => {
        const file = videoInput.files?.[0] ?? null;
        videoInput.value = '';
        if (file) setVideo(file);
      }),
    );
  }
  if (videoRemove) offs.push(on(videoRemove, 'click', () => setVideo(null)));

  /* ── mount ─────────────────────────────────────────────────────────────── */

  root.hidden = false;
  buttons();
  refresh();
  // The history starts here: the document as it is now is what undo comes back to.
  void send({ kind: 'state' });

  return {
    docId,
    state,
    undo,
    redo,
    async flush() {
      await queue;
      return docId;
    },
    async write(format, writeOptions) {
      await queue;
      return client.request('write', { docId, format, options: writeOptions });
    },
    setVideo,
    destroy() {
      destroyed = true;
      if (frame) cancelAnimationFrame(frame);
      clearTimeout(trackTimer);
      for (const off of offs) off();
      resizeObserver?.disconnect();
      if (hasVideo) setVideo(null);
      windowEl.replaceChildren();
      pool.length = 0;
      drafts.clear();
      root.hidden = true;
    },
  };
}
